import { useRef, useState } from 'react';
import { useProjectDepartments } from '../../pm/useProjectDepartments';
import { ProjectDepartment } from '../../pm/types';
import { IconChevDown } from './Icons';
import PortalMenu from './PortalMenu';

interface Props {
  /** Current department; absent = "Unassigned". */
  department?: ProjectDepartment;
  /** `null` clears the department. */
  onChange: (next: ProjectDepartment | null) => void;
}

const DepartmentPicker: React.FC<Props> = ({ department, onChange }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  // Hook returns the merged built-in + custom list, with realtime updates.
  const { departments, getDept } = useProjectDepartments();
  const current = getDept(department);
  const label = current?.label ?? 'Unassigned';

  function pick(next: ProjectDepartment | null) {
    onChange(next);
    setOpen(false);
  }

  return (
    <span className="pm-meta-pick-wrap" ref={triggerRef}>
      <button
        type="button"
        className="pm-meta-pick"
        onClick={() => setOpen((v) => !v)}
        title="Click to change department"
      >
        {current ? (
          <span className="pm-priority-dot" style={{ background: current.swatch }} />
        ) : (
          <span className="pm-priority-dot pm-priority-dot-unset" />
        )}
        <span className="pm-meta-pick-label">{label}</span>
        <IconChevDown size={12} />
      </button>
      <PortalMenu
        open={open}
        triggerRef={triggerRef}
        onDismiss={() => setOpen(false)}
        className="pm-priority-menu"
        estimatedHeight={(departments.length + 1) * 36 + 16}
      >
        {departments.map((d) => (
          <button
            key={d.id}
            className={d.id === department ? 'active' : ''}
            onClick={() => pick(d.id)}
          >
            <span className="pm-priority-dot" style={{ background: d.swatch }} />
            {d.label}
          </button>
        ))}
        <button
          className={!department ? 'active' : ''}
          onClick={() => pick(null)}
        >
          Unassigned
        </button>
      </PortalMenu>
    </span>
  );
};

export default DepartmentPicker;
